import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Filter, MapPin, Search, Bell, ChevronRight, Loader2, Navigation, Sun, Snowflake, CloudRain, Calendar, PiggyBank, Heart, Baby, Sparkles, Cloud, Thermometer } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import ActivityCard from '../components/ActivityCard';
import FilterPanel from '../components/FilterPanel';
import BottomNav from '../components/BottomNav';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

const collectionIcons = {
  sun: Sun,
  snowflake: Snowflake,
  'cloud-rain': CloudRain,
  'calendar-days': Calendar,
  'piggy-bank': PiggyBank,
  heart: Heart,
  baby: Baby,
  sparkles: Sparkles,
};

const Discover = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { t, language } = useLanguage();
  
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'for_you');
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    radius: 25,
    ageGroups: [],
    priceRange: null,
    category: null,
    indoorOutdoor: 'both',
    grandparentFriendly: false,
    buggyFriendly: false
  });

  // Near Me state
  const [userLocation, setUserLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState(null);

  // Recommendations state
  const [recommendations, setRecommendations] = useState([]);
  const [recsLoading, setRecsLoading] = useState(false);

  // Collections state
  const [collections, setCollections] = useState([]);

  // Weather state
  const [weather, setWeather] = useState(null);
  const [weatherActivities, setWeatherActivities] = useState([]);
  const [weatherTitle, setWeatherTitle] = useState('');

  const tabs = [
    { id: 'for_you', label: t('tab_for_you') },
    { id: 'near_me', label: t('tab_near_me') },
    { id: 'trending', label: t('tab_trending') },
    { id: 'budget', label: t('tab_budget') },
    { id: 'premium', label: t('tab_premium') },
    { id: 'weekend', label: t('tab_weekend') },
    { id: 'holidays', label: t('tab_holidays') }
  ];

  // Notifications state
  const [unreadNotifs, setUnreadNotifs] = useState(0);

  // Fetch collections + weather on mount
  useEffect(() => {
    const fetchCollections = async () => {
      try {
        const res = await axios.get(`${API}/api/collections`);
        setCollections(res.data.collections || []);
      } catch (err) {
        console.error('Failed to load collections:', err);
      }
    };
    const fetchWeather = async () => {
      try {
        const res = await axios.get(`${API}/api/weather/suggestions`);
        setWeather(res.data.weather);
        setWeatherActivities(res.data.activities || []);
        setWeatherTitle(res.data.suggestion_title?.[language] || res.data.suggestion_title?.en || '');
      } catch (err) {
        console.error('Failed to load weather:', err);
      }
    };
    const fetchNotifCount = async () => {
      try {
        const res = await axios.get(`${API}/api/notifications/unread-count`, { withCredentials: true });
        setUnreadNotifs(res.data.unread || 0);
      } catch (err) { /* not logged in */ }
    };
    fetchCollections();
    fetchWeather();
    if (user) fetchNotifCount();
  }, [language, user]);

  // Fetch recommendations for logged-in users
  useEffect(() => {
    if (user && activeTab === 'for_you') {
      const fetchRecs = async () => {
        setRecsLoading(true);
        try {
          const res = await axios.get(`${API}/api/recommendations?limit=5`, { withCredentials: true });
          setRecommendations(res.data.activities || []);
        } catch (err) {
          console.error('Failed to load recommendations:', err);
        } finally {
          setRecsLoading(false);
        }
      };
      fetchRecs();
    }
  }, [user, activeTab]);

  // Request geolocation when Near Me tab is selected
  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationError(t('near_me_not_supported'));
      return;
    }
    setLocationLoading(true);
    setLocationError(null);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
        setLocationLoading(false);
      },
      (err) => {
        setLocationError(t('near_me_denied'));
        setLocationLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, [t]);

  useEffect(() => {
    if (activeTab === 'near_me' && !userLocation && !locationLoading) {
      requestLocation();
    }
  }, [activeTab, userLocation, locationLoading, requestLocation]);

  // Fetch nearby activities
  const fetchNearby = useCallback(async () => {
    if (!userLocation) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        latitude: userLocation.latitude,
        longitude: userLocation.longitude,
        radius_km: filters.radius,
        limit: 20,
      });
      if (filters.category) params.append('category', filters.category);
      if (filters.priceRange) params.append('price_range', filters.priceRange);
      if (filters.indoorOutdoor && filters.indoorOutdoor !== 'both') {
        params.append('indoor_outdoor', filters.indoorOutdoor);
      }
      const res = await axios.get(`${API}/api/activities/near-me?${params}`, { withCredentials: true });
      setActivities(res.data.activities || []);
      setTotalPages(1);
    } catch (err) {
      console.error('Near me fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [userLocation, filters]);

  // Fetch standard activities
  const fetchActivities = useCallback(async (resetPage = false) => {
    if (activeTab === 'near_me') return;
    setLoading(true);
    const currentPage = resetPage ? 1 : page;
    try {
      const params = new URLSearchParams({
        tab: activeTab,
        page: currentPage,
        limit: 10
      });
      if (filters.category) params.append('category', filters.category);
      if (filters.priceRange) params.append('price_range', filters.priceRange);
      if (filters.indoorOutdoor && filters.indoorOutdoor !== 'both') {
        params.append('indoor_outdoor', filters.indoorOutdoor);
      }
      if (filters.grandparentFriendly) params.append('grandparent_friendly', 'true');
      if (filters.buggyFriendly) params.append('buggy_friendly', 'true');

      const res = await axios.get(`${API}/api/activities?${params}`, { withCredentials: true });
      if (resetPage) {
        setActivities(res.data.activities);
        setPage(1);
      } else {
        setActivities(prev => currentPage === 1 ? res.data.activities : [...prev, ...res.data.activities]);
      }
      setTotalPages(res.data.pages);
    } catch (err) {
      console.error('Error fetching activities:', err);
    } finally {
      setLoading(false);
    }
  }, [activeTab, page, filters]);

  // Main data fetcher on tab change
  useEffect(() => {
    if (activeTab === 'near_me') {
      if (userLocation) fetchNearby();
    } else {
      fetchActivities(true);
    }
  }, [activeTab, userLocation]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  const handleApplyFilters = () => {
    setShowFilters(false);
    if (activeTab === 'near_me') {
      fetchNearby();
    } else {
      fetchActivities(true);
    }
  };

  const handleResetFilters = () => {
    setFilters({
      radius: 25, ageGroups: [], priceRange: null, category: null,
      indoorOutdoor: 'both', grandparentFriendly: false, buggyFriendly: false
    });
  };

  const handleSaveToggle = (activityId, saved) => {
    setActivities(prev => prev.map(a => a.id === activityId ? { ...a, is_saved: saved } : a));
    setRecommendations(prev => prev.map(a => a.id === activityId ? { ...a, is_saved: saved } : a));
  };

  // Track activity view
  const trackView = useCallback(async (activityId) => {
    try {
      await axios.post(`${API}/api/track`, { action: 'view', activity_id: activityId }, { withCredentials: true });
    } catch (err) { /* silent */ }
  }, []);

  const loadMore = () => {
    if (page < totalPages) setPage(prev => prev + 1);
  };

  useEffect(() => {
    if (page > 1 && activeTab !== 'near_me') fetchActivities();
  }, [page]);

  const getCollectionName = (col) => col.name?.[language] || col.name?.en || '';
  const getCollectionDesc = (col) => col.description?.[language] || col.description?.en || '';

  return (
    <div className="min-h-screen bg-[#F9F8F6] pb-24">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-[#2C302E]/5">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <img src="/infobel-logo.png" alt="InfoBel" className="h-8 w-8 rounded-lg bg-[#2C302E] object-cover" />
              <div>
                <h1 className="text-xl font-semibold text-[#2C302E] font-['Outfit']" data-testid="discover-greeting">
                  {user ? `${t('discover_greeting')} ${user.name?.split(' ')[0]}!` : t('discover_title')}
                </h1>
                <p className="text-sm text-[#666B67] flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  Belgium
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={() => setShowFilters(true)}
                className="w-10 h-10 rounded-full bg-white border border-[#2C302E]/10 flex items-center justify-center"
                data-testid="filter-btn"
              >
                <Filter className="w-5 h-5 text-[#2C302E]" />
              </button>
              <button 
                onClick={() => navigate('/notifications')}
                className="w-10 h-10 rounded-full bg-white border border-[#2C302E]/10 flex items-center justify-center relative"
                data-testid="notif-btn"
              >
                <Bell className="w-5 h-5 text-[#2C302E]" />
                {unreadNotifs > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#E07A5F] rounded-full flex items-center justify-center text-[10px] text-white font-bold">
                    {unreadNotifs}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Search Bar */}
          <button
            onClick={() => navigate('/search')}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl bg-white border border-[#2C302E]/10 text-left"
            data-testid="search-btn"
          >
            <Search className="w-5 h-5 text-[#666B67]" />
            <span className="text-[#666B67]">{t('discover_search_placeholder')}</span>
          </button>
        </div>

        {/* Tabs */}
        <div className="overflow-x-auto pb-4 px-4 -mx-4 hide-scrollbar">
          <div className="flex gap-2 min-w-max px-4 max-w-2xl mx-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`tab-pill whitespace-nowrap ${activeTab === tab.id ? 'tab-pill-active' : 'tab-pill-inactive'}`}
                data-testid={`tab-${tab.id}`}
              >
                {tab.id === 'near_me' && <Navigation className="w-3.5 h-3.5 mr-1 inline" />}
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-2xl mx-auto px-4 py-6 space-y-8">

        {/* Collections Strip */}
        {collections.length > 0 && activeTab !== 'near_me' && (
          <section data-testid="collections-strip">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-[#2C302E] font-['Outfit']">{t('collections_title_short')}</h2>
              <button 
                onClick={() => navigate('/collections')}
                className="text-sm text-[#E07A5F] font-medium flex items-center gap-1"
                data-testid="view-all-collections"
              >
                {t('common_view_all')} <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <div className="overflow-x-auto -mx-4 px-4 hide-scrollbar">
              <div className="flex gap-3 min-w-max">
                {collections.filter(c => c.is_active).slice(0, 6).map((col) => {
                  const IconComp = collectionIcons[col.icon] || Sparkles;
                  return (
                    <button
                      key={col.id}
                      onClick={() => navigate(`/collections/${col.id}`)}
                      className="flex flex-col items-center gap-2 px-4 py-3 rounded-2xl bg-white border border-[#2C302E]/5 hover:border-[#2C302E]/15 transition-all min-w-[100px] card-hover"
                      data-testid={`collection-${col.id}`}
                    >
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center"
                        style={{ backgroundColor: `${col.color}15` }}
                      >
                        <IconComp className="w-6 h-6" style={{ color: col.color }} />
                      </div>
                      <span className="text-xs font-medium text-[#2C302E] text-center leading-tight max-w-[90px]">
                        {getCollectionName(col)}
                      </span>
                      <span className="text-[10px] text-[#666B67]">
                        {col.activity_count} {t('saved_activities')}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* Recommendations Section (For You tab, logged in) */}
        {activeTab === 'for_you' && weather && weatherActivities.length > 0 && (
          <section data-testid="weather-section">
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                weather.is_rainy ? 'bg-blue-100' : weather.is_cold ? 'bg-purple-100' : 'bg-amber-100'
              }`}>
                {weather.is_rainy ? <CloudRain className="w-5 h-5 text-blue-500" /> :
                 weather.is_cold ? <Snowflake className="w-5 h-5 text-purple-500" /> :
                 <Sun className="w-5 h-5 text-amber-500" />}
              </div>
              <div>
                <h2 className="text-base font-semibold text-[#2C302E] font-['Outfit']">{weatherTitle}</h2>
                {weather.temperature !== null && (
                  <p className="text-xs text-[#666B67] flex items-center gap-1">
                    <Thermometer className="w-3 h-3" /> {weather.temperature}°C in Belgium
                  </p>
                )}
              </div>
            </div>
            <div className="overflow-x-auto -mx-4 px-4 hide-scrollbar">
              <div className="flex gap-4 min-w-max">
                {weatherActivities.slice(0, 4).map((activity) => (
                  <div key={activity.id} className="w-[260px] flex-shrink-0">
                    <ActivityCard activity={activity} onSaveToggle={handleSaveToggle} variant="compact" />
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Recommended For You (For You tab, logged in) */}
        {activeTab === 'for_you' && user && recommendations.length > 0 && (
          <section data-testid="recommendations-section">
            <h2 className="text-lg font-semibold text-[#2C302E] font-['Outfit'] mb-4">
              {t('recommended_for_you')}
            </h2>
            {recsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-[#E07A5F]" />
              </div>
            ) : (
              <div className="overflow-x-auto -mx-4 px-4 hide-scrollbar">
                <div className="flex gap-4 min-w-max">
                  {recommendations.map((activity) => (
                    <div key={activity.id} className="w-[260px] flex-shrink-0" onClick={() => trackView(activity.id)}>
                      <ActivityCard activity={activity} onSaveToggle={handleSaveToggle} variant="compact" />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        {/* Near Me Content */}
        {activeTab === 'near_me' && (
          <>
            {locationLoading && (
              <div className="flex flex-col items-center justify-center py-16 gap-4" data-testid="location-loading">
                <div className="w-16 h-16 rounded-full bg-[#E07A5F]/10 flex items-center justify-center">
                  <Navigation className="w-8 h-8 text-[#E07A5F] animate-pulse" />
                </div>
                <p className="text-[#666B67] font-medium">{t('near_me_locating')}</p>
                <p className="text-sm text-[#666B67]/70">{t('near_me_allow')}</p>
              </div>
            )}
            {locationError && (
              <div className="flex flex-col items-center justify-center py-16 gap-4 text-center" data-testid="location-error">
                <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center">
                  <MapPin className="w-8 h-8 text-red-400" />
                </div>
                <p className="text-[#2C302E] font-medium">{t('near_me_error_title')}</p>
                <p className="text-sm text-[#666B67] max-w-xs">{locationError}</p>
                <button onClick={requestLocation} className="btn-primary text-sm mt-2" data-testid="retry-location-btn">
                  {t('near_me_retry')}
                </button>
              </div>
            )}
            {userLocation && !locationLoading && !locationError && activities.length > 0 && (
              <div className="mb-2">
                <p className="text-sm text-[#666B67] flex items-center gap-1">
                  <Navigation className="w-3.5 h-3.5 text-[#E07A5F]" />
                  {t('near_me_showing')} {activities.length} {t('near_me_within')} {filters.radius}km
                </p>
              </div>
            )}
          </>
        )}

        {/* Activities Feed */}
        {loading && activities.length === 0 && activeTab !== 'near_me' ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#E07A5F]" />
          </div>
        ) : (activeTab === 'near_me' && (locationLoading || (locationError && !userLocation))) ? null : activities.length === 0 && !loading ? (
          <div className="text-center py-20">
            <p className="text-[#666B67] mb-4">{t('discover_no_activities')}</p>
            <button onClick={handleResetFilters} className="text-[#E07A5F] font-medium hover:underline">
              {t('discover_clear_filters')}
            </button>
          </div>
        ) : (
          <>
            <div className="grid gap-6">
              {activities.map((activity, index) => (
                <div 
                  key={activity.id} 
                  className={`fade-in-up fade-in-up-${(index % 5) + 1}`}
                  style={{ opacity: 0 }}
                  onClick={() => trackView(activity.id)}
                >
                  <ActivityCard activity={activity} onSaveToggle={handleSaveToggle} />
                  {/* Distance badge for Near Me */}
                  {activeTab === 'near_me' && activity.distance_km !== undefined && (
                    <div className="flex items-center gap-1 mt-2 ml-1">
                      <Navigation className="w-3.5 h-3.5 text-[#E07A5F]" />
                      <span className="text-sm text-[#666B67] font-medium">{activity.distance_km} km {t('near_me_away')}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {activeTab !== 'near_me' && page < totalPages && (
              <div className="mt-8 text-center">
                <button
                  onClick={loadMore}
                  disabled={loading}
                  className="btn-secondary inline-flex items-center gap-2"
                  data-testid="load-more-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      {t('discover_load_more')}
                      <ChevronRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </main>

      <FilterPanel
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        filters={filters}
        onFilterChange={setFilters}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
      />

      <BottomNav />
    </div>
  );
};

export default Discover;
